import { AuthFacade } from '../../auth/state/auth.facade';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, combineLatest, from } from 'rxjs';
import { take, switchMap, defaultIfEmpty, map } from 'rxjs/operators';
import { IdAuthService, IdTokenError, expiresSoon } from '../../auth/services/id-auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

	private refreshing: Promise<string | null> | null = null;

	constructor(private authFacade: AuthFacade, private idAuth: IdAuthService) {

	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return combineLatest([this.authFacade.token$, this.authFacade.refreshToken$, this.authFacade.anonymousToken$]).pipe(
			take(1),
			defaultIfEmpty([null, null, null]),
			switchMap(([token, refreshToken, anonymousToken]) => from(this.freshToken(token, refreshToken)).pipe(
				map(fresh => fresh ? `Bearer ${fresh}` : anonymousToken ? `Anonymous ${anonymousToken}` : null)
			)),
			switchMap(authorization => {
				if (authorization) {
					const newRequest = req.clone({
						headers: req.headers.set('Authorization', authorization)
					});
					return next.handle(newRequest);
				} else {
					return next.handle(req);
				}
			}));
	}

	// ID-5: Pro Pool ID's access tokens live 10 minutes, so shortly before expiry the refresh token buys a new pair
	// first. One refresh at a time: Pro Pool ID rotates refresh tokens and treats a replayed one as theft.
	private freshToken(token: string | null, refreshToken: string | null): Promise<string | null> {
		if (!token || !refreshToken || !expiresSoon(token)) {
			return Promise.resolve(token);
		}
		if (!this.refreshing) {
			this.refreshing = this.idAuth.refresh(refreshToken).then(
				tokens => {
					this.authFacade.tokensRefreshed(tokens);
					return tokens.access_token;
				},
				error => {
					// Spent, revoked ("log out everywhere") or expired: the session is over. Anything else (offline,
					// Pro Pool ID down) keeps the old token; the API decides on that one.
					if (error instanceof IdTokenError && error.error === 'invalid_grant') {
						this.authFacade.signedOut();
						return null;
					}
					return token;
				}).finally(() => {
					this.refreshing = null;
				});
		}
		return this.refreshing;
	}
}
